<template>
  <div class="col-12">
    <div class="card-body px-0 pb-2">
      <div class="p-4">
        <form @submit.prevent="handleSubmit" @keydown.enter.prevent>
          <div class="row g-3">
            <!-- Title -->
            <div class="col-md-12">
              <label for="title" class="bmd-label-float">{{ $t("assignmentForm.title") }}</label>
              <input
                type="text"
                class="form-control borderless-input"
                :class="{ 'is-invalid': errors.title }"
                id="title"
                v-model.trim="formData.title"
                required
                @blur="validateField('title')"
              />
              <div class="invalid-feedback">{{ errors.title || $t("assignmentForm.requiredField") }}</div>
            </div>

            <!-- Description -->
            <div class="col-md-12">
              <label for="description" class="bmd-label-float">{{ $t("assignmentForm.description") }}</label>
              <textarea
                class="form-control borderless-input"
                :class="{ 'is-invalid': errors.description }"
                id="description"
                v-model.trim="formData.description"
                rows="3"
              ></textarea>
              <div class="invalid-feedback">{{ errors.description }}</div>
            </div>

            <!-- Due Date -->
            <div class="col-md-6">
              <label for="due_date" class="bmd-label-float">{{ $t("assignmentForm.dueDate") }}</label>
              <input
                type="datetime-local"
                class="form-control borderless-input"
                :class="{ 'is-invalid': errors.due_date }"
                id="due_date"
                v-model="formData.due_date"
                required
                :min="minDate"
                @blur="validateField('due_date')"
              />
              <div class="invalid-feedback">{{ errors.due_date || $t("assignmentForm.requiredField") }}</div>
            </div>

            <!-- Class -->
            <div class="col-md-6">
              <label for="class_id" class="bmd-label-float">{{ $t("assignmentForm.class") }}</label>
              <select
                class="form-select borderless-input"
                :class="{ 'is-invalid': errors.class_id }"
                id="class_id"
                v-model="formData.class_id"
                required
                @change="fetchClassData"
                @blur="validateField('class_id')"
              >
                <option value="">{{ $t("assignmentForm.selectClass") }}</option>
                <option
                  v-for="classItem in classes"
                  :key="classItem.id"
                  :value="classItem.id"
                >
                  {{ classItem?.name || $t("assignmentForm.unknownClass") }}
                </option>
              </select>
              <div class="invalid-feedback">{{ errors.class_id || $t("assignmentForm.requiredField") }}</div>
            </div>

            <!-- Group -->
            <div class="col-md-6">
              <label for="group_id" class="bmd-label-float">{{ $t("assignmentForm.group") }}</label>
              <select
                class="form-select borderless-input"
                :class="{ 'is-invalid': errors.group_id }"
                id="group_id"
                v-model="formData.group_id"
                required
                :disabled="!formData.class_id"
                @blur="validateField('group_id')"
              >
                <option value="">{{ $t("assignmentForm.selectGroup") }}</option>
                <option
                  v-for="group in classGroups"
                  :key="group.id"
                  :value="group.id"
                >
                  {{ group.name }}
                </option>
              </select>
              <div class="invalid-feedback">{{ errors.group_id || $t("assignmentForm.requiredField") }}</div>
            </div>

            <!-- Subject -->
            <div class="col-md-6">
              <label for="subject_id" class="bmd-label-float">{{ $t("assignmentForm.subject") }}</label>
              <select
                class="form-select borderless-input"
                :class="{ 'is-invalid': errors.subject_id }"
                id="subject_id"
                v-model="formData.subject_id"
                required
                :disabled="!formData.class_id"
                @change="fetchSubjectTeachers"
                @blur="validateField('subject_id')"
              >
                <option value="">{{ $t("assignmentForm.selectSubject") }}</option>
                <option
                  v-for="subject in filteredSubjects"
                  :key="subject.id"
                  :value="subject.id"
                >
                  {{ subject.name }}
                </option>
              </select>
              <div class="invalid-feedback">{{ errors.subject_id || $t("assignmentForm.requiredField") }}</div>
            </div>

            <!-- Teacher -->
            <div class="col-md-6" v-if="showTeacherSelect">
              <label for="teacher_id" class="bmd-label-float">{{ $t("assignmentForm.teacher") }}</label>
              <select
                class="form-select borderless-input"
                :class="{ 'is-invalid': errors.teacher_id }"
                id="teacher_id"
                v-model="formData.teacher_id"
                required
                :disabled="!formData.subject_id"
                @blur="validateField('teacher_id')"
              >
                <option value="">{{ $t("assignmentForm.selectTeacher") }}</option>
                <option
                  v-for="teacher in subjectTeachers"
                  :key="teacher.id"
                  :value="teacher.id"
                >
                  {{ teacher.username }}
                </option>
              </select>
              <div class="invalid-feedback">{{ errors.teacher_id || $t("assignmentForm.requiredField") }}</div>
            </div>

            <!-- Buttons -->
            <div class="col-12">
              <div class="d-flex gap-3 mt-3">
                <button
                  type="submit"
                  class="btn btn-primary btn-sm flex-grow-1"
                  :disabled="loading"
                >
                  <span v-if="loading">
                    <i class="fas fa-spinner fa-spin ms-2"></i>
                    {{ $t("assignmentForm.saving") }}
                  </span>
               <span v-else>
  <i class="fas fa-save ms-2"></i>
  {{ submitButtonText }}
</span>

                </button>
                <button
                  type="button"
                  class="btn btn-outline-secondary btn-sm flex-grow-1"
                  @click="handleCancel"
                  :disabled="loading"
                >
                  <i class="fas fa-times ms-2"></i>
                  {{ $t("assignmentForm.cancel") }}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>
<script setup>
import { ref, watch, onMounted, defineEmits, defineProps, computed } from "vue";
import { useToast } from "vue-toastification";
import api from "@/api";
import { useStore } from "vuex";
import { useI18n } from "vue-i18n"; 

const { t } = useI18n(); 

// Props and basic setup
const props = defineProps({
  title: String,
  submitText: {
    type: String,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  isEditMode: {
    type: Boolean,
    default: false,
  },
  initialData: {
    type: Object,
    default: () => ({})
  }
});
const submitButtonText = computed(() => props.submitText || t("assignmentForm.save"));

const store = useStore();
const currentUser = computed(() => store.state.user);
const isTeacher = computed(() => currentUser.value?.role?.slug === 'teacher');
const emit = defineEmits(["submit", "cancel", "validation-error"]);
const toast = useToast();

// Reactive data
const formData = ref({
  title: "",
  description: "",
  due_date: "",
  class_id: "",
  group_id: "",
  subject_id: "",
  teacher_id: isTeacher.value ? currentUser.value.id : "",
});

const errors = ref({});
const classes = ref([]);
const classGroups = ref([]);
const classSubjects = ref([]);
const subjectTeachers = ref([]);
const isLoadingData = ref({
  classes: false,
  groups: false,
  subjects: false,
  teachers: false
});

// Computed properties
const minDate = computed(() => {
  const now = new Date();
  now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
  return now.toISOString().slice(0, 16);
});

const filteredSubjects = computed(() => {
  if (!formData.value.class_id) return [];
  return classSubjects.value.map(item => item.subject ? item.subject : item);
});

const showTeacherSelect = computed(() => !isTeacher.value);

// Helper functions
const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const localDate = new Date(date.getTime() - (date.getTimezoneOffset() * 60000));
  return localDate.toISOString().slice(0, 16);
};

const fetchClasses = async () => {
  isLoadingData.value.classes = true;
  try {
    let response;

    if (isTeacher.value && currentUser.value?.id) {
      try {
        response = await api.get(`/teachers/${currentUser.value.id}/classes`);
      } catch (e) {
        response = await api.get('/classes', {
          params: { teacher_id: currentUser.value.id }
        });
      }
    } else {
  response = await api.get("/classes", {
      params: {
        all: true
      }
    });    }

    const payload = response?.data;
    let list = [];

    if (Array.isArray(payload?.data))       list = payload.data;
    else if (Array.isArray(payload?.classes)) list = payload.classes;
    else if (Array.isArray(payload))          list = payload;
    else                                      list = [];

    classes.value = list.map(c => c?.class ?? c);

    if (
      props.isEditMode &&
      formData.value.class_id &&
      !classes.value.some(c => Number(c.id) === Number(formData.value.class_id))
    ) {
      formData.value.class_id = "";
      classGroups.value = [];
      classSubjects.value = [];
    }
  } catch (error) {
    console.error("Error fetching classes:", error);
    toast.error(t("assignmentForm.fetchClassesError"));
  } finally {
    isLoadingData.value.classes = false;
  }
};

const fetchClassData = async (keepValues = false) => {
  if (!formData.value?.class_id) {
    classSubjects.value = [];
    classGroups.value = [];
    if (!keepValues && !props.isEditMode) {
      formData.value.subject_id = "";
      formData.value.group_id = "";
    }
    return;
  }

  isLoadingData.value.groups = true;
  isLoadingData.value.subjects = true;
  
  try {
    const [subjectsResponse, groupsResponse] = await Promise.all([
      isTeacher.value 
        ? api.get(`/teachers/${currentUser.value.id}/subjects`, {
            params: { class_id: formData.value.class_id }
          })
        : api.get(`/classes/${formData.value.class_id}/subjects`),
      api.get(`/classes/${formData.value.class_id}/groups`)
    ]);
    
    classSubjects.value = subjectsResponse.data.data || [];
    
    classGroups.value = groupsResponse.data.data.map(item => {
      if (item.group) {
        return {
          id: item.group.id,
          name: item.group.name,
          class_id: item.class_id
        };
      } else {
        return {
          id: item.id,
          name: item.name,
          class_id: item.class_id
        };
      }
    });

    console.log('Fetched groups:', classGroups.value);
    
    if (props.isEditMode) {
      const currentGroupId = Number(formData.value.group_id);
      const currentSubjectId = Number(formData.value.subject_id);
      
      if (currentSubjectId && !classSubjects.value.some(s => s.id === currentSubjectId)) {
        console.warn('Subject not found in fetched subjects, keeping existing value');
      }
      
      if (currentGroupId && !classGroups.value.some(g => g.id === currentGroupId)) {
        console.warn('Group not found in fetched groups, keeping existing value');
        formData.value.group_id = "";
      }
    } else {
      formData.value.group_id = "";
    }
  } catch (error) {
    console.error("Error fetching class data:", error);
    toast.error(t("assignmentForm.fetchClassDataError"));
    classGroups.value = [];
    classSubjects.value = [];
  } finally {
    isLoadingData.value.groups = false;
    isLoadingData.value.subjects = false;
  }
};
const fetchSubjectTeachers = async (keepValue = false) => {
  if (isTeacher.value) {
    if (!keepValue) {
      formData.value.teacher_id = currentUser.value.id;
    }
    return;
  }

  if (!formData.value.subject_id || !formData.value.class_id) {
    subjectTeachers.value = [];
    if (!keepValue) {
      formData.value.teacher_id = "";
    }
    return;
  }

  isLoadingData.value.teachers = true;
  try {
    const response = await api.get(
      `/classes/${formData.value.class_id}/subjects/${formData.value.subject_id}/teachers`
    );
    subjectTeachers.value = response.data.data || [];
    
    if (props.isEditMode && formData.value.teacher_id && !subjectTeachers.value.some(t => t.id === formData.value.teacher_id)) {
      console.warn('Teacher not found in fetched teachers, keeping existing value');
    }
  } catch (error) {
    console.error("Error fetching subject teachers:", error);
    toast.error(t("assignmentForm.fetchTeachersError"));
    subjectTeachers.value = [];
  } finally {
    isLoadingData.value.teachers = false;
  }
};

const initializeFormData = (data) => {
  if (!data) return;
  
  console.log('Initializing form data:', data);

  formData.value = {
    title: data.title || '',
    description: data.description || '',
    due_date: formatDate(data.due_date),
    class_id: data.class_id ? Number(data.class_id) : '',
    group_id: data.group_id ? Number(data.group_id) : '',
    subject_id: data.subject_id ? Number(data.subject_id) : '',
    teacher_id: isTeacher.value ? currentUser.value.id : (data.teacher_id ? Number(data.teacher_id) : '')
  };

  if (data.subject && !classSubjects.value.some(s => s.id === data.subject.id)) {
    classSubjects.value.push(data.subject);
  }

  if (data.teacher && !subjectTeachers.value.some(t => t.id === data.teacher.id)) {
    subjectTeachers.value.push(data.teacher);
  }
};

const loadEditModeData = async (data) => {
  console.log('Loading edit mode data:', data);
  
  if (classes.value.length === 0) {
    await fetchClasses();
  }
  
  if (data.class_id) {
    await fetchClassData(true);
    if (data.subject_id) {
      if (isTeacher.value) {
        formData.value.teacher_id = currentUser.value.id;
      } else {
        await fetchSubjectTeachers(true);
      }
    }
  }
};

// Validation and submission
const validateField = (field) => {
  if (!formData.value[field] && field !== "description") {
    errors.value[field] = t("assignmentForm.requiredField");
    return false;
  }
  delete errors.value[field];
  return true;
};

const validateForm = () => {
  errors.value = {};
  const validations = [
    validateField("title"),
    validateField("due_date"),
    validateField("class_id"),
    validateField("group_id"),
    validateField("subject_id"),
    validateField("teacher_id"),
  ];
  return validations.every((valid) => valid);
};

const handleSubmit = () => {
  if (!validateForm()) {
    emit("validation-error");
    toast.warning(t("assignmentForm.fixErrors"));
    return;
  }
  emit("submit", formData.value);
};

const handleCancel = () => {
  emit("cancel");
};

watch(() => props.initialData, (newData) => {
  if (newData && Object.keys(newData).length > 0) {
    console.log('Initial data changed:', newData);
    initializeFormData(newData);
    if (props.isEditMode) {
      loadEditModeData(newData);
    }
  }
}, { immediate: true, deep: true });

onMounted(async () => {
  await fetchClasses();
  
  if (isTeacher.value) {
    formData.value.teacher_id = currentUser.value.id;
  }
  
  if (props.isEditMode && props.initialData && Object.keys(props.initialData).length > 0) {
    initializeFormData(props.initialData);
    await loadEditModeData(props.initialData);
  }
});
</script>
